import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ADD_CAMPAIGN_FORM_STEPS, INPUTS_PROPS, RECIPIENT_SELECTION_TYPES } from "../../../../../enums/Enums";
import { commonActions } from "../../../../../redux/reducers/CommonReducer";
import store from "../../../../../redux/Store";
import Strings from "../../../../../resources/Strings";
import Icons from "../../../../../resources/icons/Icons";
import * as messagingActionsService from "../../../../../services/ActionsService/MessagingActionsService";
import { messagingActions } from "../../../../../redux/reducers/MessagingReducer";
import { getSitesUnderSelectedScope, isUserSubscribed } from "../../../../../utils/UsersUtils";
import {
  showDiscardChangesMessage,
  showErrorMessage, showSaveCampaignSuccessMessage, showSendCampaignSuccessMessage
} from "../../../../../utils/MessagesUtils";
import { Button } from "@mui/material";
import EllipseButton from "../../../Buttons/EllipseButton/EllipseButton";
import classes from "../WizardForm.module.css";
import CampaignDetailsStep from "../Steps/CampaignDetailsStep";
import RecipientsStep from "../Steps/RecipientsStep";
import CampaignReviewStep from "../Steps/CampaignReviewStep";
import { CampaignEntity } from "../../../../../entities/ServicesEntities/MessagingEntities/CampaignEntity";
import { getDeletedRecipientsIds, getMessagingCampaignsData, getMessagingHistoriesData } from "../../../../../utils/MessagingUtils";
import { CAMPAIGN_TYPE_LIST } from "../../../../../constants/Constants";
import { SendCampaignOperationsEntity } from "../../../../../entities/ServicesEntities/MessagingEntities/SendCampaignOperationsEntity";
import { RecipientEntity } from "../../../../../entities/ServicesEntities/MessagingEntities/RecipientEntity";
import { SendMailingListOperationsEntity } from "../../../../../entities/ServicesEntities/MessagingEntities/SendMailingListOperationsEntity";
import { MailingListEntity } from "../../../../../entities/ServicesEntities/MessagingEntities/MailingListEntity";
import { UtronUserEntity } from "../../../../../entities/ServicesEntities/UserEntities/UtronUserEntity";
import { removeDuplicatesInArray } from "../../../../../utils/CommonUtils";

const AddCampaignForm = (props) => {
  const allScopes = useSelector((state) => state.user?.allScopes);
  const sites = useSelector((state) => state.user.sites);
  const selectedScope = useSelector((state) =>  state.user?.selectedScope);
  const selectedSites = getSitesUnderSelectedScope(sites, allScopes, selectedScope);

  const gmtUser = useSelector((state) => state.user?.gmtUser);
  const mailingLists = useSelector((state) => state.messaging.mailingListsData?.mailingLists);
  const currentScopeUsers = useSelector((state) => state.messaging.currentScopeUsers);
  const currentGmtUsers = useSelector((state) => state.user.currentGmtUsers);
  const currentUsers = currentScopeUsers.concat(currentGmtUsers);
  const allIds = removeDuplicatesInArray(currentUsers.map(u => u.utronUser.id));

  const [formData, setFormData] = useState({
    campaignId: props?.isReview ? props?.campaign?.id : props?.isEditMode ? props?.id : 0,
    campaignType: props?.isReview ? CAMPAIGN_TYPE_LIST.find(i => i.value === props?.campaign?.campaignType) : props?.isEditMode ? CAMPAIGN_TYPE_LIST.find(i => i.value === props?.campaignType) : CAMPAIGN_TYPE_LIST[0],
    campaignName: props?.isReview ? props?.campaign?.name : props?.isEditMode && props?.isDuplicateMode ? `${props?.name}_Copy` : props?.isEditMode ? props?.name : "",
    emailSubject: props?.isReview ? props?.campaign?.subject : props?.isEditMode ? props?.subject : "",
    messageBody: props?.isReview ? props?.campaign?.body : props?.isEditMode ? props?.body : "",
    //link: props?.isReview ? props?.campaign?.link : props?.isEditMode ? props?.link : "",
    attachment: props?.isReview ? props?.campaign?.attachment : props?.isEditMode ? props?.attachment !== null ? props?.attachment : [] : [],
    attachmentName: props?.isReview ? props?.campaign?.attachmentName : props?.isEditMode ? props?.attachmentName !== null ? props?.attachmentName : null : null,
    recipients: [],
    isStepSubmitted: false
  });
  
  const [stateData, setStateData] = useState({
    currentStep: props?.currentStep ? props?.currentStep : ADD_CAMPAIGN_FORM_STEPS.CAMPAIGN_DETAILS,
    totalSteps: 3,
    isCurrentStepValid: false,
    isCurrentStepHasValues: false
  });

  const [isSaved, setIsSaved] = useState(false);
  const [initialFormData, setInitialFormData] = useState({...formData});
  const isOpenChangesExists = useSelector((state) => state.common.isOpenChangesExists);
  const sideModal = useSelector((state) => state.common.sideModal);

  useEffect(() => { 
    // handle save changes modal checks in all modes except isReview mode
    if (!props?.isReview) {
      // compare initial values versus current formData (check if user changed inputs)
      // localIsOpenChangesExists - represents the check result in the current cycle
      let localIsOpenChangesExists = false; 
  
      // isOpenChangesExists - represents the check result in the previous cycle
      // once isOpenChangesExists is true - no need to check again
      // currently campainType change doesn't count
      if (!isOpenChangesExists && (
          /*initialFormData.campaignType.value !== formData.campaignType.value || */
          initialFormData.campaignName !== formData.campaignName ||
          initialFormData.emailSubject !== formData.emailSubject || 
          initialFormData.messageBody !== formData.messageBody ||
          initialFormData.attachment?.length < formData.attachment?.length || 
          initialFormData.recipients?.length < formData.recipients?.length)) {
          localIsOpenChangesExists = true;
        }
  
      // once user changed an input, set modalCloseMessage, and set isOpenChangesExists true to disable next checks
      if (localIsOpenChangesExists && !isOpenChangesExists && !isSaved && sideModal?.modalCloseMessage === null) {
        store.dispatch(commonActions.setSideModal({modalCloseMessage: showDiscardChangesMessage}));
        store.dispatch(commonActions.setIsOpenChangesExists(true));
      }
  
      return () => {
        // unmount - on form close reset changes indication back to false
        store.dispatch(commonActions.setIsOpenChangesExists(false));
      }
    }
  }, [formData]);
  
  const handleChange = (newProp) => {
    setFormData({ ...formData, ...newProp });
  };

  const handleStateDataChange = (newProp) => {
    setStateData({ ...stateData, ...newProp });
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormData({ ...formData, ["isStepSubmitted"]: true });
  };

  const getCurrentStepButtons = () => {
    switch (stateData.currentStep) {
      case ADD_CAMPAIGN_FORM_STEPS.CAMPAIGN_DETAILS:
        return <EllipseButton onClick={next} disabled={!stateData.isCurrentStepHasValues}>{Strings.SAVE_AND_CONTINUE}</EllipseButton>
      case ADD_CAMPAIGN_FORM_STEPS.RECIPIENTS:
        return (
          <>
            <EllipseButton onClick={next} disabled={!stateData.isCurrentStepHasValues}>{Strings.NEXT}</EllipseButton>
            <EllipseButton onClick={skip} isCancelButton={true} >{Strings.SKIP}</EllipseButton>
          </>
        )
      case ADD_CAMPAIGN_FORM_STEPS.CAMPAIGN_REVIEW:
        return (
          <>
            <EllipseButton onClick={send}>{Strings.SEND}</EllipseButton>
            <EllipseButton onClick={skip} isCancelButton={true} >{Strings.SKIP}</EllipseButton>
          </>
        )
    }
  };

  const saveAndContinue = () => {
    const { campaignType, campaignName, emailSubject, messageBody, attachment, attachmentName, recipients } = formData;
    let action = null;

    // Attachment cases:
    // When attachment is array - user added new file / replaced - taking the datauri + base64 content file
    // When attachment is not array - existing file - taking the absolute blob storage link
    // When attachment not added - attachment is null

    // check type
    const campaign = new CampaignEntity(
      props?.isEditMode ? props.id : 0,
      campaignName,
      campaignType.value,
      new Date(),
      gmtUser?.utronUser?.id,
      emailSubject, 
      messageBody,
      null,
      attachment?.length > 0 ? Array.isArray(attachment) ? attachment[1] : attachment : null,
      attachment?.length > 0 ? Array.isArray(attachment) ? attachment?.[0]?.name : attachmentName : null,
      selectedSites?.[0]?.id,
      false
    );

    if (props?.isEditMode) {
      if (props?.isDuplicateMode) {
        // Compare difference of new user's changed values to current opened campaign
        const campaignToDuplicate = new CampaignEntity(
          0, // props?.isEditMode ? props.id : 0
          campaignName !== props?.name ? campaignName : props?.name,
          campaignType.value !== props?.type ? campaignType.value : props?.type,
          props?.createdDate,
          gmtUser?.utronUser?.id,
          emailSubject !== props?.subject ? emailSubject : props?.subject, 
          messageBody !== props?.body ? messageBody : props?.body,
          null,
          attachment?.length > 0 ? Array.isArray(attachment) ? attachment[1] : attachment : null,
          attachmentName !== props?.attachmentName ? attachmentName : props?.attachmentName,
          selectedSites?.[0]?.id,
          false
        );

        action = messagingActionsService.addCampaign(campaignToDuplicate);
      }
      else {
        action = messagingActionsService.updateCampaign(campaign);
      }
    }
    else {
      action = messagingActionsService.addCampaign(campaign);
    }

    action.then(response => {
      if (response.data.isSuccess) {
        let campaignsData = getMessagingCampaignsData(response.data.entity, selectedSites?.[0]);
        let updatedSelectionModel = campaignsData?.campaigns?.find(c => c.id === props.id);  // campaignsData?.campaigns[campaignsData?.campaigns?.length - 1];
        store.dispatch(messagingActions.setCampaignsData(campaignsData));
        store.dispatch(messagingActions.setMessagingSelectionModel(updatedSelectionModel));

        // update locally the added/updated campaign for the next steps for display
        let campaigns = campaignsData?.campaigns;
        let currentCampaign = {};
        if (props.isEditMode && props.isDuplicateMode) {
          // on duplicate - same as add
          // set added Campaign as the currentCampaign
          currentCampaign = campaigns[campaigns.length - 1];
        }
        else if (props.isEditMode && !props.isDuplicateMode) {
          // on update
          currentCampaign = updatedSelectionModel;
        }
        else {
          // add
          // set added Campaign as the currentCampaign
          currentCampaign = campaigns[campaigns.length - 1];
        }

        // update formData
        handleChange({
          [INPUTS_PROPS.CAMPAIGN_ID] : currentCampaign.id,
          [INPUTS_PROPS.CAMPAIGN_TYPE]: CAMPAIGN_TYPE_LIST.find(i => i.value === currentCampaign.campaignType),
          [INPUTS_PROPS.CAMPAIGN_NAME] : currentCampaign.name,
          [INPUTS_PROPS.EMAIL_SUBJECT]: currentCampaign.subject,
          [INPUTS_PROPS.MESSAGE_BODY] : currentCampaign.body, 
          [INPUTS_PROPS.ATTACHMENT]: currentCampaign.attachment,
          [INPUTS_PROPS.IS_STEP_SUBMITTED] : false
        });

        showSaveCampaignSuccessMessage();

        // cancel the show open changes message
        store.dispatch(commonActions.setSideModal({modalCloseMessage: null}));
        store.dispatch(commonActions.setIsOpenChangesExists(false));
        setIsSaved(true);
      }
      else {
        showErrorMessage(response.data?.error);
        store.dispatch(commonActions.closeSideModal());
        store.dispatch(commonActions.setIsShowLoadingSpinner(false));
      }
    }).catch(error => {
      showErrorMessage(error);
      store.dispatch(commonActions.closeSideModal());
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
    });
  }

  const send = () => {
    const { 
      campaignId,
      campaignType,
      campaignName,
      emailSubject,
      messageBody,
      attachment,
      attachmentName,
      recipients
    } = formData;

    // build campaign
    const campaign = new CampaignEntity(
      campaignId,
      campaignName,
      campaignType?.value,
      props?.campaign?.createdDate,
      gmtUser?.utronUser?.id,
      emailSubject,
      messageBody,
      null,
      attachment?.length > 0 ? attachment : null,
      attachmentName,
      selectedSites?.[0]?.id,
      false
    );

    // Send campaign
    // handle recipients
    // identifiy which is mailing list / single users
    const selectedMailingLists = [];
    const selectedUsersIds = [];
    
    recipients.forEach(item => {
      // convert to entities
      if (item?.identifierType === RECIPIENT_SELECTION_TYPES.MAILING_LIST) {
        const foundedMailingList = mailingLists?.find(ml => ml?.id === item?.identifierId);
        selectedMailingLists.push(foundedMailingList);       
      }
      else {
        const foundedUserId = currentUsers.find(user => user.utronUser?.id === item.identifierId);
        selectedUsersIds.push(foundedUserId);
      }
    });

    // handle mailingLists
    // map userIds to recipient obj & make sure users are subscribed 
    let mappedMailingLists = selectedMailingLists.map(ml => {
      const recipients = ml.mailingListToUsers.map(mlto => {
        const foundedUser = currentUsers.find(cu => cu.utronUser?.id === mlto.userId);
        const isUserSubscribedRes = isUserSubscribed(foundedUser?.utronUser);
        if (foundedUser && isUserSubscribedRes) {
          let recipient = new RecipientEntity(foundedUser.utronUser?.identityId, foundedUser.utronUser?.id, foundedUser.phoneNumber, foundedUser.email, null);
          return recipient;
        }
      }).filter(u => u); // remove undefined

      const mailingList = new MailingListEntity(
        ml.id,
        ml.name,
        ml.dateCreated,
        ml.createdBy,
        selectedSites?.[0]?.id
      );

      const sendMailingListOperationsEntity = new SendMailingListOperationsEntity(mailingList, recipients);

      return sendMailingListOperationsEntity;
    });

    let mappedUsers = selectedUsersIds.map(user => {
        const foundedUser = currentUsers.find(cu => cu.utronUser?.id === user?.utronUser?.id);
        const isUserSubscribedRes = isUserSubscribed(foundedUser?.utronUser);
        if (foundedUser && isUserSubscribedRes) {
          let recipient = new RecipientEntity(foundedUser.utronUser?.identityId, foundedUser.utronUser?.id, foundedUser.phoneNumber, foundedUser.email, null);
          return recipient;
        }
    }).filter(u => u); // remove undefined

    const sentByUser = new UtronUserEntity(
      gmtUser?.utronUser?.id, 
      gmtUser?.utronUser?.identityId, 
      gmtUser?.utronUser?.firstName,
      gmtUser?.utronUser?.lastName
    );

    const sendCampaignOperationsEntity = new SendCampaignOperationsEntity(campaign, mappedMailingLists, mappedUsers, sentByUser);

    messagingActionsService.sendCampaign(sendCampaignOperationsEntity, true, false)
    .then((response => {
      if (response.data.isSuccess) {
        // Get updated campaigns data
        let campaignsData = {};
        messagingActionsService.getCampaignsBySiteId(selectedSites?.[0]?.id, true, false)
        .then((response => {
          if (response.data.isSuccess) {
            campaignsData = getMessagingCampaignsData(response.data.entity, selectedSites?.[0]);
            store.dispatch(messagingActions.setCampaignsData(campaignsData));

            // get messaging histories by campaigns ids
            let allCampaignsIds = campaignsData?.campaigns.concat(campaignsData?.deletedCampaigns)?.map(c => c.id);
            messagingActionsService.getMessagingHistoriesByCampaignsIds(allCampaignsIds, true, false) // true, true
            .then((response => {
              if (response.data.isSuccess) {           
                let allCampaigns = campaignsData?.campaigns.concat(campaignsData?.deletedCampaigns);
                
                // get deleted recipients ids
                const deletedRecipientsIds = getDeletedRecipientsIds(allIds, response.data.entity);           
                let messagingHistoriesData = getMessagingHistoriesData(response.data.entity, allCampaigns, deletedRecipientsIds, selectedSites?.[0]);

                store.dispatch(messagingActions.setMessagingHistoriesData(messagingHistoriesData));
                let messagingHistoryIds = messagingHistoriesData?.messagingHistories?.map(mh => mh.id);
                
                // this is the closing spinner call
                messagingActionsService.getMessagingHistoryToMailingListsByMessagingHistoryIds(messagingHistoryIds, true, true)
                .then((response => {
                  if (response.data.isSuccess) {
                    store.dispatch(messagingActions.setMessagingHistoryToMailingLists(response.data.entity));

                    // show campaign send success
                    showSendCampaignSuccessMessage();

                    // close side modal
                    store.dispatch(commonActions.closeSideModal());
                  }
                  else {
                    showErrorMessage(response.data?.error);
                    store.dispatch(commonActions.setIsShowLoadingSpinner(false));
                  }
                }))
                .catch(error => {
                  showErrorMessage(error);
                  store.dispatch(commonActions.setIsShowLoadingSpinner(false));
                });           
              }
              else {
                showErrorMessage(response.data?.error);
                store.dispatch(commonActions.setIsShowLoadingSpinner(false));
              }
            }))
            .catch(error => {
              showErrorMessage(error);
              store.dispatch(commonActions.setIsShowLoadingSpinner(false));
            });
          }
          else {
            showErrorMessage(response.data?.error);
            store.dispatch(commonActions.setIsShowLoadingSpinner(false));
          }
        })).catch(error => {
          showErrorMessage(error);
          store.dispatch(commonActions.setIsShowLoadingSpinner(false));
        });
      }
      else {
        showErrorMessage(response.data?.error);
        store.dispatch(commonActions.setIsShowLoadingSpinner(false));
      }
    }))
    .catch(error => {
      showErrorMessage(error);
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
    });
  };

  const prev = () => {
    let currentStep = stateData.currentStep;
    currentStep = currentStep <= stateData.totalSteps ? currentStep - 1 : currentStep; 
    setStateData({ ...stateData, currentStep: currentStep });
    setFormData({ ...formData, ["isStepSubmitted"]: false });
  };

  const next = () => {
    setFormData({ ...formData, ["isStepSubmitted"]: true });
    if (stateData.isCurrentStepValid) {
        let currentStep = stateData.currentStep;
        currentStep =
          currentStep >= stateData.totalSteps - 1
            ? stateData.totalSteps
            : currentStep + 1;
        setStateData({ ...stateData, currentStep: currentStep });
        setFormData({ ...formData, ["isStepSubmitted"]: false });

        if (stateData.currentStep === ADD_CAMPAIGN_FORM_STEPS.CAMPAIGN_DETAILS) {
          saveAndContinue();
        }
    }
  };

  const skip = () => {
    store.dispatch(commonActions.closeSideModal());
  };

  return (
    <>
      {stateData.currentStep === ADD_CAMPAIGN_FORM_STEPS.CAMPAIGN_REVIEW && (
        <div className={classes.prev}>
          <img src={Icons.LEFT_ARROW} />
          <Button onClick={prev}>{Strings.BACK}</Button>
        </div>
      )}
      <form name="AddCampaignForm" onSubmit={handleSubmit}>
        <div className={classes.formContainer}>
          <div className={classes.stepContainer}>
            <CampaignDetailsStep
              isEditMode={props?.isEditMode}
              isDuplicateMode={props?.isDuplicateMode}
              currentStep={stateData.currentStep}
              handleChange={handleChange}
              handleStateDataChange={handleStateDataChange}
              {...formData}
            />
            <RecipientsStep
              isReview={props?.isReview}
              isEditMode={props?.isEditMode}
              isDuplicateMode={props?.isDuplicateMode}
              currentStep={stateData.currentStep}
              handleChange={handleChange}
              handleStateDataChange={handleStateDataChange}
              {...formData}
            />
            <CampaignReviewStep 
              //campaign={props?.campaign}
              isEditMode={props?.isEditMode}
              isDuplicateMode={props?.isDuplicateMode}
              currentStep={stateData.currentStep}
              handleChange={handleChange}
              {...formData}
            />
          </div>
          {stateData.currentStep === ADD_CAMPAIGN_FORM_STEPS.CAMPAIGN_REVIEW && (
            <div>
              <div>
                  <h4>{Strings.READY_TO_SEND}</h4>  
                  <h6 className={classes.sendSubTitle}>{Strings.READY_TO_SEND_REVIEW_MESSAGE}</h6>                       
              </div>
            </div>
            )}
          <div className={classes.next}>
            {getCurrentStepButtons()}
          </div>
        </div>
      </form>
    </>
  );
};

export default AddCampaignForm;
